import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, MessageCircle, Home, Calculator } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import config from './config';
import { getTranslation } from './i18n';
import LanguageSelector from './components/LanguageSelector';
import TariffCalculator from './components/TariffCalculator';

// 취소선 마크다운(~~텍스트~~)을 제거하는 함수
const removeStrikethrough = (text) => {
  if (!text) return text;
  // ~~텍스트~~ 패턴을 찾아서 취소선 마커와 내용을 모두 제거
  // 정규식: ~~로 시작하고 ~~로 끝나는 패턴 (non-greedy, 여러 줄 포함)
  // [\s\S]는 모든 문자(줄바꿈 포함)를 의미
  return text.replace(/~~[\s\S]*?~~/g, '');
};

const DocumentChatbot = () => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [queryProgress, setQueryProgress] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [pretrainedInfo, setPretrainedInfo] = useState(null);
  const [serverStatus, setServerStatus] = useState('checking'); // 'connected', 'disconnected', 'checking'
  const [language, setLanguage] = useState('ko'); // 기본값: 한국어
  const [selectedImage, setSelectedImage] = useState(null); // 모달에 표시할 이미지
  const [isModalOpen, setIsModalOpen] = useState(false); // 모달 열림/닫힘 상태
  const [imageScale, setImageScale] = useState(1); // 이미지 확대/축소 비율
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 }); // 이미지 위치
  const [isTariffCalculatorOpen, setIsTariffCalculatorOpen] = useState(false); // 운송료 계산기 모달 상태
  const messagesEndRef = useRef(null);

  // 배치 응답 방식에서는 토큰 누적 함수가 필요 없음

  // 메시지 끝으로 스크롤
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // 이미지 확대/축소 핸들러
  const handleImageWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // 휠 방향에 따른 확대/축소
    const delta = e.deltaY > 0 ? -0.2 : 0.2;
    const newScale = Math.max(0.1, Math.min(5, imageScale + delta));
    setImageScale(newScale);
    
    console.log('휠 이벤트 발생:', { deltaY: e.deltaY, newScale });
  };

  // 이미지 드래그 핸들러
  const handleImageMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX - imagePosition.x;
    const startY = e.clientY - imagePosition.y;

    const handleMouseMove = (e) => {
      setImagePosition({
        x: e.clientX - startX,
        y: e.clientY - startY
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // 이미지 리셋 핸들러
  const resetImageTransform = () => {
    setImageScale(1);
    setImagePosition({ x: 0, y: 0 });
  };

  // 모달 열기/닫기 핸들러
  const openImageModal = (imageSrc) => {
    setSelectedImage(imageSrc);
    setIsModalOpen(true);
    resetImageTransform();
  };

  const closeImageModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
    resetImageTransform();
  };


  useEffect(() => {
    console.log('📊 메시지 배열 업데이트됨:', {
      messagesLength: messages.length,
      messages: messages
    });
    
    // 메시지가 추가되면 항상 자동 스크롤 (로딩 상태 무관)
    if (messages.length > 0) {
      // 약간의 지연을 주어 DOM 업데이트 후 스크롤
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [messages]);

  // 사전 훈련된 문서 정보 가져오기
  const fetchPretrainedInfo = useCallback(async () => {
    try {
      console.log('🔍 사전 훈련된 문서 정보 가져오기 시작...');
      
      // 5초 타임아웃 설정
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${config.API_BASE_URL}/preloaded`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        setPretrainedInfo(data);
        setServerStatus('connected');
        console.log('✅ 사전 훈련된 문서 정보 가져오기 성공:', data);
      } else {
        console.error('❌ 서버 응답 오류:', response.status, response.statusText);
        setServerStatus('disconnected');
      }
    } catch (error) {
      console.error('❌ 사전 훈련된 문서 정보 가져오기 실패:', error);
      if (error.name === 'AbortError') {
        console.error('❌ 서버 연결 타임아웃 (5초)');
      }
      setServerStatus('disconnected');
    }
  }, []);

  // 서버 연결 테스트
  const testServerConnection = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      
      const response = await fetch(`${config.API_BASE_URL}/preloaded`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      console.error('❌ 서버 연결 테스트 실패:', error);
      return false;
    }
  };

  // 컴포넌트 마운트 시 문서 정보 가져오기
  useEffect(() => {
    const initializeData = async () => {
      await fetchPretrainedInfo();
    };
    
    initializeData();
  }, [fetchPretrainedInfo]);

  // 질문 전송
  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    // 세션 ID 생성 (첫 번째 메시지인 경우)
    if (!sessionId) {
      const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
    }

    const userMessage = {
      type: 'user',
      content: inputText,
      timestamp: new Date()
    };

    // 사용자 메시지 추가
    setMessages(prev => {
      const newMessages = [...prev, userMessage];
      console.log('📝 사용자 메시지 추가됨:', {
        totalMessages: newMessages.length,
        userMessage: userMessage,
        allMessages: newMessages
      });
      return newMessages;
    });
    
    const currentInput = inputText;
    setInputText('');
    setIsLoading(true);
      setQueryProgress(getTranslation(language, 'system.serverConnecting'));

    try {
      // 1. 서버 연결 테스트
      console.log('🔍 [단계 1] 서버 연결 테스트 중...');
      const serverConnected = await testServerConnection();
      
      if (!serverConnected) {
        throw new Error(getTranslation(language, 'system.serverUnavailable'));
      }

      console.log('✅ [단계 1] 서버 연결 확인 완료');
      setQueryProgress(getTranslation(language, 'system.aiGenerating'));

      // 2. SSE 스트리밍 요청
      console.log('📡 [단계 2] SSE 스트리밍 요청 시작:', currentInput);
      
      // 빈 AI 메시지 미리 추가
      // messages.length는 state 업데이트 전 값이므로 +1 (user) 고려
      const aiMessageIndex = messages.length + 1;
      const aiMessage = {
        type: 'bot',
        content: '',
        sources: [],
        confidence: null,
        ai_model: 'exaone3.5:latest',
        retrievedChunks: 0,
        images: [],
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
      console.log('🤖 AI 메시지 추가됨: 인덱스', aiMessageIndex);
      
      // SSE 스트리밍 요청 (Express 프록시 사용)
      const streamUrl = `${config.API_BASE_URL}/query-stream`;
      console.log('🔗 [SSE] URL:', streamUrl);
      
      const requestBody = {
        query: currentInput,
        max_tokens: 2000,
        use_hybrid_search: true,
        session_id: sessionId,
        language: language
      };
      console.log('📤 [SSE] 요청 데이터:', requestBody);
      
      const response = await fetch(streamUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('📡 [SSE] 응답 상태:', response.status);
      console.log('📡 [SSE] 응답 헤더:', {
        contentType: response.headers.get('Content-Type'),
        cacheControl: response.headers.get('Cache-Control'),
        connection: response.headers.get('Connection')
      });

      if (!response.ok) {
        console.error('❌ [SSE] 응답 실패:', response.status, response.statusText);
        throw new Error(`서버 오류: ${response.status}`);
      }

      console.log('📥 [단계 3] SSE 스트리밍 시작');
      
      const reader = response.body.getReader();
      console.log('📖 [SSE] Reader 생성됨');
      
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let sources = [];
      let timing = null;
      let buffer = '';  // 불완전한 라인을 위한 버퍼
      let chunkReadCount = 0;

      while (true) {
        const { done, value } = await reader.read();
        chunkReadCount++;
        
        if (done) {
          console.log(`✅ [SSE] 스트림 읽기 완료 (총 ${chunkReadCount}개 청크 읽음)`);
          break;
        }

        console.log(`📥 [SSE] 청크 ${chunkReadCount} 수신: ${value.length} bytes`);
        
        const chunk = decoder.decode(value, { stream: true });
        console.log(`📝 [SSE] 디코딩된 청크 (처음 100자):`, chunk.substring(0, 100));
        
        buffer += chunk;  // 버퍼에 추가
        const lines = buffer.split('\n');
        
        console.log(`📋 [SSE] 분할된 라인 수: ${lines.length}`);
        
        // 마지막 라인은 불완전할 수 있으므로 버퍼에 보관
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonData = line.substring(6).trim();
            if (!jsonData) {
              console.log('⚠️ [SSE] 빈 데이터 라인 스킵');
              continue;
            }
            
            console.log(`🔍 [SSE] JSON 파싱 시도: ${jsonData.substring(0, 50)}...`);
            
            try {
              const data = JSON.parse(jsonData);
              console.log('✅ [SSE] 파싱 성공! 타입:', data.type);

              if (data.type === 'start') {
                console.log('🚀 스트리밍 시작');
              } else if (data.type === 'status') {
                setQueryProgress(data.message);
                console.log('📊', data.message);
              } else if (data.type === 'chunk') {
                // 실시간으로 텍스트 추가
                accumulatedText += data.content;
                const newContent = accumulatedText;
                console.log(`📝 [SSE] 청크 추가: +${data.content.length}자, 총 ${newContent.length}자`);
                
                // "죄송합니다."로 시작하는 경우 가드레일 메시지로 판단하고 이미지 제거
                if (accumulatedText.trim().startsWith('죄송합니다.') || 
                    accumulatedText.trim().startsWith('I apologize') ||
                    accumulatedText.trim().startsWith('Je m\'excuse') ||
                    accumulatedText.trim().startsWith('申し訳ございません')) {
                  console.log('🚫 [가드레일 감지] 이미지 제거 플래그 설정');
                  // 이미지 배열 초기화
                  setMessages(prev => {
                    const newMessages = [...prev];
                    newMessages[aiMessageIndex] = {
                      ...newMessages[aiMessageIndex],
                      content: newContent,
                      images: []  // 이미지 제거
                    };
                    return newMessages;
                  });
                } else {
                  setMessages(prev => {
                    const newMessages = [...prev];
                    newMessages[aiMessageIndex] = {
                      ...newMessages[aiMessageIndex],
                      content: newContent
                    };
                    console.log(`🔄 [SSE] 메시지 업데이트됨: ${newContent.length}자`);
                    return newMessages;
                  });
                }
              } else if (data.type === 'timing') {
                console.log('⏱️ [SSE] 타이밍:', data.stage, ':', data.time, '초');
              } else if (data.type === 'complete') {
                const newSources = data.sources || [];
                const newImages = data.images || [];
                const newTiming = data.timing;
                sources = newSources;
                timing = newTiming;
                
                console.log('📦 [SSE] 완료 데이터 수신:', {
                  sources: newSources.length,
                  images: newImages.length,
                  imagesDetail: newImages.map((img, idx) => ({
                    index: idx,
                    source: img?.source,
                    hasImage: !!img?.image,
                    imageLength: img?.image?.length
                  })),
                  timing: newTiming
                });
                
                // 가드레일 메시지인 경우 이미지 제거
                const isGuardrailMessage = accumulatedText.trim().startsWith('죄송합니다.') || 
                                          accumulatedText.trim().startsWith('I apologize') ||
                                          accumulatedText.trim().startsWith('Je m\'excuse') ||
                                          accumulatedText.trim().startsWith('申し訳ございません');
                
                const finalImages = isGuardrailMessage ? [] : newImages;
                
                if (isGuardrailMessage && newImages.length > 0) {
                  console.log('🚫 [가드레일 감지] 이미지 제거됨:', newImages.length, '개');
                }
                
                // 최종 메시지 업데이트
                setMessages(prev => {
                  const newMessages = [...prev];
                  newMessages[aiMessageIndex] = {
                    ...newMessages[aiMessageIndex],
                    sources: newSources,
                    images: finalImages,  // 가드레일 메시지면 빈 배열
                    timing: newTiming
                  };
                  console.log('🔄 [SSE] 최종 메시지 업데이트:', {
                    index: aiMessageIndex,
                    content: newMessages[aiMessageIndex].content.length,
                    images: newMessages[aiMessageIndex].images.length
                  });
                  return newMessages;
                });

                console.log('✅ 스트리밍 완료');
                console.log('⏱️ 타이밍:', timing);
                console.log('🖼️ 이미지:', newImages.length, '개');
                if (newImages.length > 0) {
                  console.log('🖼️ 이미지 상세:', newImages);
                }
              } else if (data.type === 'error') {
                console.error('❌ 스트리밍 오류:', data.message);
                throw new Error(data.message);
              }
            } catch (parseError) {
              // JSON 파싱 오류만 무시 (불완전한 청크)
              console.warn('⚠️ [SSE] JSON 파싱 실패:', parseError.message);
              console.warn('⚠️ [SSE] 실패한 데이터:', jsonData.substring(0, 100));
            }
          } else {
            console.log(`📄 [SSE] 비데이터 라인: ${line.substring(0, 50)}`);
          }
        }
      }

      setQueryProgress('');
      console.log('✅ [단계 5] 응답 처리 완료');
    } catch (error) {
      console.error('❌ [오류] 요청 실패:', error);
      console.error('❌ [오류 상세]:', error.stack);
      
      // 이미 AI 메시지가 추가된 경우 오류 메시지로 교체하지 않음
      if (!messages.some(m => m.type === 'bot' && m.content.length > 0)) {
        const errorMessage = {
          type: 'error',
          content: `${getTranslation(language, 'system.errorOccurred')}: ${error.message}`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      } else {
        console.log('⚠️ 답변이 이미 출력되었으므로 오류 메시지를 표시하지 않음');
      }
    } finally {
      setIsLoading(false);
      setQueryProgress('');
    }
  };

  // 엔터 키 처리
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  // 대화 기록 삭제
  const clearMessages = () => {
    setMessages([]);
    setSessionId(null); // 세션 ID도 초기화
  };

  // 이미지 클릭 핸들러 (기존 함수를 새로운 함수로 교체)
  const handleImageClick = (imageItem) => {
    openImageModal(imageItem);
  };

  // 모달 닫기 핸들러 (기존 함수를 새로운 함수로 교체)
  const closeModal = () => {
    closeImageModal();
  };

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isModalOpen) {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      // 모달이 열릴 때 body 스크롤 방지
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen, closeModal]);

  return (
    <div className="h-screen overflow-hidden relative" style={{
      backgroundImage: 'url(/background.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }}>
      {/* 배경 오버레이 (가독성 향상) */}
      <div className="absolute inset-0 bg-white/30 backdrop-blur-sm"></div>
      
      <div className="max-w-5xl mx-auto h-full shadow-lg rounded-2xl bg-white/95 border border-gray-200 relative z-10">
        {/* 밝은 테마 채팅 컨테이너 */}
        <div className="flex flex-col h-full bg-white rounded-2xl">
          {/* 상단 헤더 */}
          <div className="flex items-center justify-between px-6 py-3 border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${
                serverStatus === 'connected' ? 'bg-green-400 animate-pulse' :
                serverStatus === 'disconnected' ? 'bg-red-400' :
                'bg-yellow-400 animate-pulse'
              }`}></div>
              <span className="text-sm text-gray-600 font-medium">
                {serverStatus === 'connected' ? getTranslation(language, 'ui.connected') :
                 serverStatus === 'disconnected' ? getTranslation(language, 'ui.disconnected') :
                 getTranslation(language, 'ui.checking')}
              </span>
              {pretrainedInfo && pretrainedInfo.ready && (
                <span className="text-sm text-gray-500">
                  {/* • {pretrainedInfo.count}개 문서 학습됨 */}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <LanguageSelector 
                currentLanguage={language} 
                onLanguageChange={setLanguage} 
              />
              <button
                onClick={() => setIsTariffCalculatorOpen(true)}
                className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-all flex items-center"
              >
                <Calculator size={16} className="mr-1" />
                운송료 계산
              </button>
              <button
                onClick={() => setMessages([])}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-all flex items-center"
              >
                <Home size={16} className="mr-1" />
                {getTranslation(language, 'ui.home')}
              </button>
              <button
                onClick={clearMessages}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-all"
              >
                {getTranslation(language, 'ui.newChat')}
              </button>
            </div>
          </div>

          {/* 채팅 영역 - 독립적인 스크롤 */}
          <div className="flex-1 overflow-hidden bg-white">
            {/* 메시지 컨테이너 - 독립적인 스크롤 */}
            <div 
              className="chat-message-container px-4 pt-1 pb-1"
              style={{
                scrollBehavior: 'smooth'
              }}
            >
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-full max-w-2xl mx-auto text-center">
                  <div className="w-20 h-20 bg-blue-100 text-blue-500 flex items-center justify-center rounded-full shadow-sm mb-6">
                    <MessageCircle size={42} />
                  </div>
                  <h1 className="text-3xl font-extrabold text-gray-800 mb-3">
                    {getTranslation(language, 'main.title')}
                  </h1>
                  <p className="text-gray-600 max-w-lg mb-8 leading-relaxed">
                    {getTranslation(language, 'main.subtitle')}
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
                    <div className="p-5 border border-gray-200 bg-blue-50/40 hover:bg-blue-100 rounded-xl text-left transition-all cursor-pointer hover:shadow-md" onClick={() => {
                      setInputText(getTranslation(language, 'main.examples.platform.query'));
                    }}>
                      <h3 className="font-semibold text-blue-800 mb-1">{getTranslation(language, 'main.examples.platform.title')}</h3>
                      <p className="text-sm text-gray-600">{getTranslation(language, 'main.examples.platform.description')}</p>
                    </div>
                    <div className="p-5 border border-gray-200 bg-blue-50/40 hover:bg-blue-100 rounded-xl text-left transition-all cursor-pointer hover:shadow-md" onClick={() => {
                      setInputText(getTranslation(language, 'main.examples.operations.query'));
                    }}>
                      <h3 className="font-semibold text-blue-800 mb-1">{getTranslation(language, 'main.examples.operations.title')}</h3>
                      <p className="text-sm text-gray-600">{getTranslation(language, 'main.examples.operations.description')}</p>
                    </div>
                    <div className="p-5 border border-gray-200 bg-blue-50/40 hover:bg-blue-100 rounded-xl text-left transition-all cursor-pointer hover:shadow-md" onClick={() => {
                      setInputText(getTranslation(language, 'main.examples.center.query'));
                    }}>
                      <h3 className="font-semibold text-blue-800 mb-1">{getTranslation(language, 'main.examples.center.title')}</h3>
                      <p className="text-sm text-gray-600">{getTranslation(language, 'main.examples.center.description')}</p>
                    </div>
                    <div className="p-5 border border-gray-200 bg-blue-50/40 hover:bg-blue-100 rounded-xl text-left transition-all cursor-pointer hover:shadow-md" onClick={() => {
                      setInputText(getTranslation(language, 'main.examples.platformTech.query'));
                    }}>
                      <h3 className="font-semibold text-blue-800 mb-1">{getTranslation(language, 'main.examples.platformTech.title')}</h3>
                      <p className="text-sm text-gray-600">{getTranslation(language, 'main.examples.platformTech.description')}</p>
                    </div>
                  </div>
                  
                  {/* 추가 예시 질문들 */}
                  <div className="mt-6 w-full max-w-2xl">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">{getTranslation(language, 'main.additionalQuestions')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <button 
                        className="p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-all bg-white text-gray-700 hover:border-blue-300"
                        onClick={() => {
                          const queries = {
                            'ko': "주안부평 스마트 물류플랫폼을 이용했을 때 어떤 혜택들이 있나요?",
                            'en': "What benefits are available when using the Juanbupeong Smart Logistics Platform?",
                            'fr': "Quels avantages sont disponibles lors de l'utilisation de la plateforme logistique intelligente Juanbupeong?",
                            'ja': "主安富平スマート物流プラットフォームを利用した際のメリットは何ですか？"
                          };
                          setInputText(queries[language] || queries['ko']);
                        }}
                      >
                        {getTranslation(language, 'main.examples.automation')}
                      </button>
                      <button 
                        className="p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-all bg-white text-gray-700 hover:border-blue-300"
                        onClick={() => {
                          const queries = {
                            'ko': "주안부평 스마트 물류플랫폼 컨설팅 신청 방법은 무엇인가요?",
                            'en': "How can I apply for Juanbupeong Smart Logistics Platform consulting?",
                            'fr': "Comment puis-je demander un conseil pour la plateforme logistique intelligente Juanbupeong?",
                            'ja': "主安富平スマート物流プラットフォームコンサルティングの申請方法は何ですか？"
                          };
                          setInputText(queries[language] || queries['ko']);
                        }}
                      >
                        {getTranslation(language, 'main.examples.iot')}
                      </button>
                      <button 
                        className="p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-all bg-white text-gray-700 hover:border-blue-300"
                        onClick={() => {
                          const queries = {
                            'ko': "주안부평 스마트 물류플랫폼의 대상 기업은 누구인가요?",
                            'en': "Which companies are eligible for the Juanbupeong Smart Logistics Platform?",
                            'fr': "Quelles entreprises sont éligibles pour la plateforme logistique intelligente Juanbupeong?",
                            'ja': "主安富平スマート物流プラットフォームの対象企業は誰ですか？"
                          };
                          setInputText(queries[language] || queries['ko']);
                        }}
                      >
                        {getTranslation(language, 'main.examples.optimization')}
                      </button>
                      <button 
                        className="p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-all bg-white text-gray-700 hover:border-blue-300"
                        onClick={() => {
                          const queries = {
                            'ko': "주안부평 스마트 물류플랫폼의 사업 개요는 무엇인가요?",
                            'en': "What is the project overview of the Juanbupeong Smart Logistics Platform?",
                            'fr': "Quel est l'aperçu du projet de la plateforme logistique intelligente Juanbupeong?",
                            'ja': "主安富平スマート物流プラットフォームの事業概要は何ですか？"
                          };
                          setInputText(queries[language] || queries['ko']);
                        }}
                      >
                        {getTranslation(language, 'main.examples.digitalTwin')}
                      </button>
                    </div>
                  </div>
                </div>
                ) : (
                <div className="space-y-4 max-w-3xl mx-auto pt-4 pb-4">
                  {/* 디버깅: 메시지 개수 표시 */}
                  <div className="text-xs text-gray-400 mb-2">
                    총 {messages.length}개 메시지
                  </div>
                  {messages.map((message, index) => (
                    <div key={`${index}-${message.timestamp.getTime()}`} className={`flex ${
                      message.type === 'user' ? 'justify-end' : 'justify-start'
                    }`}>
                      {message.type !== 'user' && message.content && message.content.length > 0 && (
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                          <MessageCircle size={16} className="text-blue-500" />
                        </div>
                      )}
                      <div className={`max-w-[80%] message-bubble ${
                        message.type === 'user' 
                          ? 'bg-blue-500 text-white shadow-md' 
                          : message.type === 'error'
                          ? 'bg-red-50 text-red-800 border border-red-200'
                          : 'bg-gray-100 text-gray-900 border border-gray-200'
                      } px-4 py-3 rounded-lg ${message.type === 'user' ? 'rounded-br-sm' : 'rounded-bl-sm'}
                      ${message.type === 'bot' && !message.content ? 'hidden' : ''}`}>
                          {message.type === 'bot' ? (
                            <div className="prose prose-sm max-w-none dark:prose-invert">
                              {/* 디버깅: 이미지가 포함된 메시지인 경우 로그 출력 */}
                              {message.images && message.images.length > 0 && (
                                <div style={{display: 'none'}}>
                                  {console.log('🖼️ 렌더링 - 이미지 포함 메시지:', {
                                    contentLength: message.content ? message.content.length : 0,
                                    contentPreview: message.content ? message.content.substring(0, 100) : 'null',
                                    imagesCount: message.images.length
                                  })}
                                </div>
                              )}
                              <div className="prose dark:prose-invert max-w-none">
                                <ReactMarkdown 
                                  remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
                                  components={{
                                    // 테이블 내부 요소들의 스타일링 개선
                                    table: ({ children, ...props }) => (
                                      <div className="overflow-x-auto">
                                        <table {...props} className="min-w-full border-collapse">
                                          {children}
                                        </table>
                                      </div>
                                    ),
                                    th: ({ children, ...props }) => (
                                      <th {...props} className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-left font-semibold">
                                        {children}
                                      </th>
                                    ),
                                    td: ({ children, ...props }) => (
                                      <td {...props} className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                                        {children}
                                      </td>
                                    ),
                                    // 테이블 내부의 강조, 링크, 코드 등 마크다운 요소 지원
                                    strong: ({ children, ...props }) => (
                                      <strong {...props} className="font-semibold text-gray-900" style={{ color: '#111827', fontWeight: '700' }}>
                                        {children}
                                      </strong>
                                    ),
                                    em: ({ children, ...props }) => (
                                      <em {...props} className="italic text-gray-700 dark:text-gray-300">
                                        {children}
                                      </em>
                                    ),
                                    code: ({ children, ...props }) => (
                                      <code {...props} className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-sm font-mono">
                                        {children}
                                      </code>
                                    ),
                                    a: ({ children, href, ...props }) => (
                                      <a {...props} href={href} className="text-blue-600 dark:text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer">
                                        {children}
                                      </a>
                                    ),
                                    // 줄바꿈 처리
                                    br: () => <br className="my-1" />
                                  }}
                                >
                                  {removeStrikethrough(message.content)}
                                </ReactMarkdown>
                              </div>
                              
                              {/* 이미지 표시 (AI 답변에서 이미지가 포함된 경우) */}
                              {(() => {
                                const hasImages = message.images && message.images.length > 0;
                                const validImages = hasImages ? message.images.filter(img => img && img.image) : [];
                                
                                console.log('🔍 [IMG-RENDER] 렌더링 조건 체크:', {
                                  messageType: message.type,
                                  hasImages: hasImages,
                                  totalImages: message.images?.length || 0,
                                  validImages: validImages.length,
                                  renderCondition: validImages.length > 0,
                                  imagesDetail: message.images?.map((img, idx) => ({
                                    index: idx,
                                    hasImg: !!img,
                                    source: img?.source,
                                    hasImageData: !!img?.image,
                                    imageLength: img?.image?.length,
                                    imagePrefix: img?.image?.substring(0, 30)
                                  }))
                                });
                                
                                return null;
                              })()}
                              
                              {message.images && message.images.length > 0 && message.images.some(img => img && img.image) && (
                                <div className="mt-4">
                                  <div className="text-xs text-gray-500 mb-2">관련 이미지:</div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {message.images.filter(img => img && img.image).map((imageItem, index) => {
                                      if (!imageItem) return null;
                                      
                                      // 이미지 데이터 처리
                                      let imageSrc = imageItem.image;
                                      if (!imageSrc.startsWith('data:')) {
                                        imageSrc = `data:image/png;base64,${imageSrc}`;
                                      }
                                      
                                      console.log(`이미지 ${index + 1} 데이터:`, {
                                        source: imageItem.source,
                                        imageLength: imageItem.image ? imageItem.image.length : 0,
                                        imagePrefix: imageItem.image ? imageItem.image.substring(0, 50) : 'null',
                                        finalSrcLength: imageSrc.length
                                      });
                                      
                                      return (
                                        <div key={index} className="border border-gray-300 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                                          <img 
                                            src={imageSrc}
                                            alt={`관련 이미지 ${index + 1}`}
                                            className="w-full h-auto max-h-64 object-contain bg-white cursor-pointer hover:opacity-90 transition-opacity duration-200"
                                            onClick={() => handleImageClick(imageItem)}
                                            onLoad={() => {
                                              console.log(`이미지 ${index + 1} 로드 성공:`, imageItem.source);
                                            }}
                                            onError={(e) => {
                                              console.error('이미지 로드 실패:', {
                                                source: imageItem.source,
                                                imageData: imageItem.image ? imageItem.image.substring(0, 100) : 'null',
                                                finalSrc: imageSrc.substring(0, 100)
                                              });
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                          <div className="px-2 py-1 bg-gray-50 text-xs text-gray-600 border-t">
                                            {/* 출처: {imageItem.source} */}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
                          )}
                          
                        {/* AI 답변의 간소화된 추가 정보 */}
                        {message.type === 'bot' && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <div className="flex items-center space-x-3">
                                {/* {message.ai_model && <span>AI 모델: {message.ai_model}</span>} */}
                                {/* <span>신뢰도: {message.confidence !== null ? (message.confidence === 0 ? '측정되지 않음' : (message.confidence * 100).toFixed(1) + '%') : '측정되지 않음'}</span> */}
                              </div>
                              <span>{message.timestamp.toLocaleTimeString(
                                language === 'ko' ? 'ko-KR' : 
                                language === 'en' ? 'en-US' : 
                                language === 'fr' ? 'fr-FR' : 
                                language === 'ja' ? 'ja-JP' : 'ko-KR'
                              )}</span>
                            </div>
                            {message.sources && message.sources.length > 0 && (
                              <div className="mt-2 text-xs text-gray-500">
                                {/* 참고: {message.sources.slice(0, 2).join(', ')}
                                {message.sources.length > 2 && ` 외 ${message.sources.length - 2}개`} */}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {message.type === 'user' && (
                          <div className="text-right mt-2">
                            <span className="text-xs text-blue-200">
                              {message.timestamp.toLocaleTimeString(
                                language === 'ko' ? 'ko-KR' : 
                                language === 'en' ? 'en-US' : 
                                language === 'fr' ? 'fr-FR' : 
                                language === 'ja' ? 'ja-JP' : 'ko-KR'
                              )}
                            </span>
                          </div>
                        )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
              {/* 밝은 테마 로딩 표시 - AI 답변 스트리밍 시작 전에만 표시 */}
              {isLoading && !messages.some(m => m.type === 'bot' && m.content && m.content.length > 0) && (
                <div className="flex justify-start max-w-3xl mx-auto">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                    <MessageCircle size={16} className="text-blue-500" />
                  </div>
                  <div className="bg-gray-100 border border-gray-200 px-4 py-3 rounded-lg rounded-bl-sm max-w-xs">
                    <div className="flex items-center">
                      <div className="flex space-x-1 mr-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                      <span className="text-gray-700 text-sm">{getTranslation(language, 'ui.thinking')}</span>
                    </div>
                    {queryProgress && (
                      <div className="text-xs text-gray-600 mt-2">
                        {queryProgress}
                      </div>
                    )}
                  </div>
                </div>
              )}
                
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* 밝은 테마 입력 영역 - 항상 하단에 고정 */}
          <div className="border-t bg-white p-4">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center space-x-3">
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={getTranslation(language, 'ui.sendMessage')}
                  className="flex-1 border border-gray-300 rounded-full px-4 py-3 resize-none focus:ring-2 focus:ring-blue-400 focus:outline-none text-gray-800"
                  rows={1}
                  disabled={isLoading}
                  style={{ minHeight: '48px', maxHeight: '120px' }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputText.trim() || isLoading}
                  className="w-12 h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send size={20} />
                </button>
              </div>
              
              <div className="flex justify-center mt-3">
                <p className="text-xs text-gray-500">
                  {getTranslation(language, 'ui.shiftEnter')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 이미지 모달 - 전체 화면 */}
      {isModalOpen && selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
          onClick={closeModal}
        >
          <div 
            className="relative w-full h-full bg-white overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 모달 헤더 - 상단 고정 */}
            <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-white border-b border-gray-200 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800">{getTranslation(language, 'ui.imageViewer.title')}</h3>
              <div className="flex items-center space-x-4">
                {/* 확대 버튼들 */}
                <button
                  onClick={() => setImageScale(Math.max(0.1, imageScale - 0.2))}
                  className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm transition-colors"
                >
                  {getTranslation(language, 'ui.imageViewer.zoomOut')}
                </button>
                <button
                  onClick={resetImageTransform}
                  className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm transition-colors"
                >
                  {getTranslation(language, 'ui.imageViewer.reset')}
                </button>
                <button
                  onClick={() => setImageScale(Math.min(5, imageScale + 0.2))}
                  className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm transition-colors"
                >
                  {getTranslation(language, 'ui.imageViewer.zoomIn')}
                </button>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-800 transition-colors duration-200 text-2xl font-bold"
                  aria-label={getTranslation(language, 'ui.imageViewer.close')}
                >
                  ×
                </button>
              </div>
            </div>
            
            {/* 이미지 컨테이너 - 전체 화면 */}
            <div 
              className="relative w-full h-full overflow-hidden bg-gray-50"
              style={{ paddingTop: '80px' }} // 헤더 높이만큼 여백
              onWheel={handleImageWheel}
              onWheelCapture={handleImageWheel}
              tabIndex={0}
            >
                  {(() => {
                    let imageSrc = selectedImage.image;
                    if (!imageSrc.startsWith('data:')) {
                      imageSrc = `data:image/png;base64,${imageSrc}`;
                    }
                    return (
                      <img
                        src={imageSrc}
                        alt="확대된 이미지"
                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg"
                        style={{ 
                          cursor: 'grab',
                          transform: `translate(-50%, -50%) translate(${imagePosition.x}px, ${imagePosition.y}px) scale(${imageScale})`,
                          transition: 'transform 0.1s ease-out'
                        }}
                        onMouseDown={handleImageMouseDown}
                        onWheel={handleImageWheel}
                        onError={(e) => {
                          console.error('모달 이미지 로드 실패:', selectedImage.source);
                          e.target.style.display = 'none';
                        }}
                        draggable={false}
                      />
                    );
                  })()}
            </div>
            
            {/* 하단 정보 표시 */}
            <div className="absolute bottom-0 left-0 right-0 z-10 p-4 bg-white border-t border-gray-200 shadow-sm">
              <div className="text-center">
                <div className="text-sm text-gray-700">
                  {getTranslation(language, 'ui.imageViewer.zoomLevel')}: {Math.round(imageScale * 100)}%
                </div>
                <div className="text-xs text-gray-500">
                  {getTranslation(language, 'ui.imageViewer.instructions')}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 운송료 계산기 모달 */}
      <TariffCalculator 
        isOpen={isTariffCalculatorOpen}
        onClose={() => setIsTariffCalculatorOpen(false)}
        language={language}
      />
    </div>
  );
};

export default DocumentChatbot;